import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import useAuth from '../../auth/useAuth';
import { useSoundStatus } from '../../context/SoundStatusContext';

import { calculateTimeInSeconds, parseDescription } from '../../utils';

import Intro from '../UI/game/intro';
import Header from '../UI/game/header';
import Modal from '../UI/modal';
import Column from '../UI/game/column';
import FooterMessage from '../UI/game/footer-message';

import {
  generalInfoQuery,
  itemOptionsQuery,
  itemQuestionsQuery,
  itemsQuery,
  makeDecision,
  getDecisions,
  updateGameSession
} from './api';

import { gameQuestionsLimit } from './data';

import { shuffleArray } from '../../utils';

import './styles.css';

import Background from './images/sorting-backgrounds/sorting-background.jpg';
import NikeLogo from './images/sorting-companies/Nike.png';

import CountdownSound from './sounds/countdown.mp3';
import GameProcessSound from './sounds/game-process.mp3';
import ClickSound from './sounds/click.mp3';
import AnswerCorrectSound from './sounds/answer-correct.mp3';
import AnswerWrongSound from './sounds/answer-wrong.mp3';

const getDeepestLevelItems = (options) => {
  const deepestItems = [];

  function traverse(node) {
    if (!node.children || node.children.length === 0) {
      deepestItems.push({id: node.id, itemId: node.itemId});
    } else {
      node.children.forEach(child => traverse(child));
    }
  }

  options.forEach(item => traverse(item));

  return deepestItems;
}

const findDeepestParentId = (itemParents, options, currentDepth = 0, maxDepthInfo = { depth: 0, id: null }) => {
  options.forEach(option => {
    if (itemParents.includes(option.itemId)) {
      if (currentDepth >= maxDepthInfo.depth) { // Use >= to include top-level elements
        maxDepthInfo.depth = currentDepth;
        maxDepthInfo.id = option.id;
      }
    }
    if (option.children && option.children.length > 0) {
      findDeepestParentId(itemParents, option.children, currentDepth + 1, maxDepthInfo);
    }
  });
  return maxDepthInfo.id;
};


export default function ClassificationScramble({levelNumber, scenarioLevels}) {
  const countdownEffect = useRef(new Audio(CountdownSound));
  const gameProcessEffect = useRef(new Audio(GameProcessSound));
  const clickEffect = useRef(new Audio(ClickSound));
  const answerCorrectEffect = useRef(new Audio(AnswerCorrectSound));
  const answerWrongEffect = useRef(new Audio(AnswerWrongSound));

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const currentGameProcessEffect = gameProcessEffect.current;
  
    return () => {
      if (currentGameProcessEffect) {
        currentGameProcessEffect.pause();
      }
    };
  }, [location]);
  
  const { getUser, getCurrentClassificationGameId } = useAuth();
  const user = getUser();
  
  const gameId = getCurrentClassificationGameId();

  const { soundStatus } = useSoundStatus();

  const { data: generalInfo } = useQuery(generalInfoQuery());
  const gamesSessions = generalInfo?.sessions;

  const [currentGameSessionId, setCurrentGameSessionId] = useState(0);

  useEffect(() => {
    if(gamesSessions && gameId && user) {
      const currentGameSession = gamesSessions.find(session => session.user === user?.id && session.game === gameId && !session.completed);
      
      if (currentGameSession) {
        setCurrentGameSessionId(currentGameSession?.id);
      } else {
        const completedGameSession = gamesSessions
          .filter(session => session.user === user?.id && session.game === gameId && session.completed)
          .reduce((maxSession, session) => (session.id > (maxSession?.id || 0) ? session : maxSession), null);
  
        setCurrentGameSessionId(completedGameSession?.id);
      }
    }
  }, [gamesSessions, gameId, user]);

  useEffect(() => {
    if(gameId) {
      if (gamesSessions && !gamesSessions.some(session => session.user === user?.id && session.is_ready === true && session.game === gameId)) {
        navigate('/classification/level/1/tutorial');
      }
    }
  }, [gamesSessions, navigate, user?.id, gameId]);

  const [scenarioId, setScenarioId] = useState(0);

  const [scenarioLevel, setScenarioLevel] = useState({
    description: '',
    fallingSpeed: 1,
    enabled: false
  });

  const [isPlaying, setIsPlaying] = useState(false);

  const [placedItems, setPlacedItems] = useState({});

  const games = generalInfo?.games;

  const itemOptionsResponse = useQuery(itemOptionsQuery({scenarioId})) || {};

  const itemQuestionsResponse = useQuery(itemQuestionsQuery(scenarioLevel.id)) || {};

  const itemsResponse = useQuery(itemsQuery()) || {};

  const [isGameBelongsToTournament, setIsGameBelongsToTournament] = useState(null);
  
  const [leftIntroTitle, setLeftIntroTitle] = useState(null);
  const [leftIntroSubtitle, setLeftIntroSubtitle] = useState(null);
  const [leftIntroDescription, setLeftIntroDescription] = useState(null);

  const [rightIntroTitle, setRightIntroTitle] = useState(null);
  const [rightIntroSubtitle, setRightIntroSubtitle] = useState(null);
  const [rightIntroDescription, setRightIntroDescription] = useState(null);

  const [isIntroContentCentered, setIsIntroContentCentered] = useState(false);
  const [isIntroSubtitleContentReversed, setIsIntroSubtitleContentReversed] = useState(false);
  useEffect(() => {
    if(gameId && scenarioLevel && games) {
      const currentGame = games.find(game => game.id === gameId);
      const isGameBelongsToTournament = currentGame?.tournament_ids?.length > 0;
      setIsGameBelongsToTournament(isGameBelongsToTournament);
      if(isGameBelongsToTournament) {
        setLeftIntroTitle('Team 1');
        setLeftIntroSubtitle('0 pts');
        setLeftIntroDescription('(Your Team)');

        setRightIntroTitle('Team 2');
        setRightIntroSubtitle('0 pts');
        setRightIntroDescription('(Your Team)');

        setIsIntroContentCentered(true);
        setIsIntroSubtitleContentReversed(true);
      } else {
        setLeftIntroTitle(`Level ${levelNumber}`);
        const { subtitle, description } = parseDescription(scenarioLevel.description);
        setLeftIntroSubtitle(subtitle);
        setRightIntroDescription(description);
      }
    }
  }, [games, gameId, levelNumber, scenarioLevel]);

  const { data: decisionsResponse, isSuccess: isDecisionsSuccess } = useQuery({
    queryKey: ['decisionsResponse', { levelNumber, userId: user?.id, gameId, gameSessionId: currentGameSessionId }],
    queryFn: () => getDecisions({ levelNumber, userId: user?.id, gameId, gameSessionId: currentGameSessionId }),
    enabled: !!user?.id && levelNumber !== undefined && !!gameId && !!currentGameSessionId,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    if(games) {
      const game = games.find(game => game.game_type.includes('Account Classification') && game.id === gameId);

      if(game && game.scenario) {
        setScenarioId(game.scenario);
      }
    }
  }, [games, gameId]);
  
  useEffect(() => {
    if(scenarioLevels.length) {
      const scenarioLevel = scenarioLevels.find(scenarioLevel => scenarioLevel.level === levelNumber);
      setScenarioLevel({
        id: scenarioLevel?.id,
        description: scenarioLevel?.description,
        fallingSpeed: scenarioLevel?.falling_speed * 1000,
        duration: calculateTimeInSeconds(scenarioLevel?.duration || '00:00:00'),
      });
    }
  }, [levelNumber, scenarioLevels]);

  const [options, setOptions] = useState([]);

  const [totalScore, setTotalScore] = useState(0);
  const [shouldScorePlaySound, setShouldScorePlaySound] = useState(false);

  const [itemsToBeSorted, setItemsToBeSorted] = useState([]);

  useEffect(() => {
    const itemOptions = itemOptionsResponse.data || [];
    const itemQuestions = itemQuestionsResponse.data || [];
    const items = itemsResponse.data || [];
    
    if(
      itemOptions.length &&
      itemQuestions.length &&
      items.length &&
      isDecisionsSuccess
    ) {
      const decisions = decisionsResponse || [];
      
      const scenarioLevelItemOptions = itemOptions.filter(itemOption => itemOption.scenario_level === scenarioLevel.id);

      const createItemObject = (item, children = []) => {
        const scenarioLevelItemOption = itemOptions.find(scenarioLevelItemOption => {
          if (item && 'id' in item) {
            return scenarioLevelItemOption.account_item === item.id;
          }
          return false;
        });
        
        const hierarchicalItem = {
          id: scenarioLevelItemOption && scenarioLevelItemOption.id,
          order: scenarioLevelItemOption && scenarioLevelItemOption.order,
          name: item && item.name,
          description: item && item.description,
          children: Array.isArray(children) ? children : [children],
          itemId: item && item.id
        };
      
        if (item && item.parents.length) {
          let parentItemIdWithOption = item.parents.find(parentId => {
            return scenarioLevelItemOptions.some(option => option.account_item === parentId);
          });
          
          if(!parentItemIdWithOption) {
            parentItemIdWithOption = item.parents.find(parentId => {
              return itemOptions.some(option => option.account_item === parentId);
            });
          }
      
          if (parentItemIdWithOption) {
            const parentItem = items.find(possibleParentItem => possibleParentItem.id === parentItemIdWithOption);
            if (parentItem) {
              return createItemObject(parentItem, [hierarchicalItem]);
            }
          } else {
            return hierarchicalItem;
          }
        } else {
          return hierarchicalItem;
        }
      }

      const hierarchicalOptions = scenarioLevelItemOptions.map(scenarioLevelItemOption => {
        const relatedItem = items.find(item => item.id === scenarioLevelItemOption.account_item);
        return createItemObject(relatedItem);
      });
      
      function mergeAndConcatenateDuplicates(items) {
        const uniqueItemsMap = new Map();
      
        items.forEach(item => {
          if (!uniqueItemsMap.has(item.id)) {
            uniqueItemsMap.set(item.id, { ...item, children: item.children ? [...item.children] : [] });
          } else {
            const existingItem = uniqueItemsMap.get(item.id);
            if (item.children && item.children.length > 0) {
              existingItem.children = existingItem.children.concat(item.children);
            }
          }
        });
      
        uniqueItemsMap.forEach((value, key) => {
          if (value.children && value.children.length > 0) {
            value.children = mergeAndConcatenateDuplicates(value.children);
          }
        });
      
        return Array.from(uniqueItemsMap.values());
      }

      function sortOptionsDataByOrder(data) {
        const sortedData = data.sort((a, b) => a.order - b.order);
      
        sortedData.forEach(item => {
          if (item.children && item.children.length > 0) {
            item.children = sortOptionsDataByOrder(item.children);
          }
        });
      
        return sortedData;
      }
      
      const optionsChildrenMerged = mergeAndConcatenateDuplicates(hierarchicalOptions);
      const sortedOptionsData = sortOptionsDataByOrder(optionsChildrenMerged);
      setOptions([...sortedOptionsData]);

      const questionsData = itemQuestions.map(itemQuestion => {
        const relatedItem = items.find(item => item.id === itemQuestion.account_item);
        return {
          id: itemQuestion.id,
          order: itemQuestion.order,
          name: relatedItem && relatedItem.name,
          description: relatedItem && relatedItem.description,
          parents: relatedItem && relatedItem.parents,
          isCorrectlyPlaced: false
        }
      });
      
      const correctDecisions = decisions.filter(decision => decision.is_correct === true);
      
      const correctQuestionIds = new Set(correctDecisions.flatMap(decision => decision.question));
      const [wrongQuestionsToBePlaced, correctQuestionsToBePlaced] = questionsData.reduce((result, question) => {
        if (correctQuestionIds.has(question.id)) {
          result[1].push(question);
        } else {
          result[0].push(question);
        }
        return result;
      }, [[], []]);

      let placedItemsByDropArea = {};
      correctQuestionsToBePlaced.forEach(item => {
        const deepestDropAreaId = findDeepestParentId(item.parents, sortedOptionsData);
        if (deepestDropAreaId) {
          placedItemsByDropArea[deepestDropAreaId] = placedItemsByDropArea[deepestDropAreaId] || [];
          placedItemsByDropArea[deepestDropAreaId].push({ ...item, isCorrectlyPlaced: true });
        }
      });

      let randomWrongQuestionsToBePlaced = [];
      if(correctQuestionsToBePlaced.length < gameQuestionsLimit) {
        const wrongDecisions = decisions.filter(decision => decision.is_correct === false);
        const wrongDecisionsIdsToAdd = new Set(wrongDecisions.flatMap(decision => decision.question));

        const questionsWithWrongDecisions = wrongQuestionsToBePlaced.filter(question => wrongDecisionsIdsToAdd.has(question.id));
        const questionsNeeded = gameQuestionsLimit - correctQuestionsToBePlaced.length - questionsWithWrongDecisions.length;
        const remainingQuestions = wrongQuestionsToBePlaced.filter(question => !wrongDecisionsIdsToAdd.has(question.id));
  
        const shuffled = remainingQuestions.sort(() => 0.5 - Math.random());
        const randomQuestions = shuffled.slice(0, Math.max(0, questionsNeeded));
        
        randomWrongQuestionsToBePlaced = [...questionsWithWrongDecisions, ...randomQuestions];
      }

      const numWrongQuestions = randomWrongQuestionsToBePlaced.length;
      const numWrongQuestionsInCorrectArea = Math.ceil(0.2 * numWrongQuestions);
      const numWrongQuestionsInRandomAreas = numWrongQuestions - numWrongQuestionsInCorrectArea;
      
      let prePlacedItemsRandomIterator = 0;

      const deepestLevelItems = getDeepestLevelItems(sortedOptionsData);

      const shuffledWrongQuestionsToBePlaced = shuffleArray(randomWrongQuestionsToBePlaced);

      shuffledWrongQuestionsToBePlaced.forEach(item => {
        let dropAreaId = 0;
        if(prePlacedItemsRandomIterator <= numWrongQuestionsInRandomAreas) {
          const filteredDeepestLevels = deepestLevelItems.filter(deepestLevelItem => !item.parents.includes(deepestLevelItem.itemId));
          const randomIndex = Math.floor(Math.random() * filteredDeepestLevels.length);
          dropAreaId = filteredDeepestLevels[randomIndex]?.id;

          prePlacedItemsRandomIterator++;
        } else {
          dropAreaId = findDeepestParentId(item.parents, sortedOptionsData);
        }
        
        // const deepestDropAreaId = findDeepestParentId(randomItem.parents, sortedOptionsData);
        // console.log('deepestDropAreaId', deepestDropAreaId);
      
        if (dropAreaId) {
          placedItemsByDropArea[dropAreaId] = placedItemsByDropArea[dropAreaId] || [];
          placedItemsByDropArea[dropAreaId].push({ ...item });
        }
      });

      // Update the state with the final placedItems
      setPlacedItems({...placedItemsByDropArea});

      const [totalElapsedTime, totalPointsScored] = correctDecisions.reduce(([totalTime, totalPoints], decision) => {
        let timeInSeconds = 0;
        if (decision.time_elapsed) {
          timeInSeconds = calculateTimeInSeconds(decision.time_elapsed);
        }

        totalTime += timeInSeconds;
        totalPoints += decision.score_points || 0;

        return [totalTime, totalPoints];
      }, [0, 0]);

      setTime(totalElapsedTime);
      setTotalScore(totalPointsScored);

      if(randomWrongQuestionsToBePlaced.length) {
        setItemsToBeSorted(randomWrongQuestionsToBePlaced);
        setModalToShow('countdown');
      } else {
        setModalToShow('well-done');
      }
    }
  }, [itemOptionsResponse.data, itemQuestionsResponse.data, itemsResponse.data, scenarioLevel.id, decisionsResponse, levelNumber, user?.id, isDecisionsSuccess]);

  const decisionMutation = useMutation({
    mutationFn: (data) => makeDecision(data)
  });
  
  const updateGameSessionMutation = useMutation({
    mutationFn: (data) => updateGameSession(data)
  });

  const [isFooterMessageShown, setIsFooterMessageShown] = useState(false);
  const [footerMessageType, setFooterMessageType] = useState();
  const [footerMessageDescription, setFooterMessageDescription] = useState();

  const showFooterMessage = (type, description) => {
    setFooterMessageType(type);
    setFooterMessageDescription(description);
    setIsFooterMessageShown(true);
  }

  const [isModalOverlayVisible, setIsModalOverlayVisible] = useState(true);
  const [isModalSmall, setIsModalSmall] = useState(false);
  const [modalToShow, setModalToShow] = useState(null);

  const [modalContent, setModalContent] = useState(
    <div className="modal-center vertical">
      <div><span>Game start!</span></div>
      <div><span className='modal-counter'>3</span></div>
    </div>
  );

  const [time, setTime] = useState(0);
  const timeRef = useRef(time);
  useEffect(() => {
    timeRef.current = time;
  }, [time]);

  useEffect(() => {
    if (!isPlaying) return;
    const timerInterval = setInterval(() => {
      const newTime = timeRef.current + 1;
      setTime(newTime);
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [isPlaying, scenarioLevel]);
  
  const [showIntro, setShowIntro] = useState(true);

  const updateModalTimeContent = () => {
    let countdown = 3;
  
    const countdownInterval = setInterval(() => {
      if (countdown === 0) {
        clearInterval(countdownInterval);
        setModalToShow(null);
        setIsPlaying(true);
  
        if (soundStatus === 'enabled') {
          gameProcessEffect.current.volume = 0.09;
          gameProcessEffect.current.loop = true;
          gameProcessEffect.current.play().catch(e => console.error('Error playing game process sound:', e));
        }
      } else {
        setModalContent(
          <div className="modal-center vertical">
            <div><span>Game start!</span></div>
            <div><span>{countdown}</span></div>
          </div>
        );
        countdown--;
  
        // Play countdown sound
        if (soundStatus === 'enabled') {
          countdownEffect.current.play().catch(e => console.error('Error playing countdown sound:', e));
        }
      }
    }, 1000);
  
    // Clean up interval on unmount or when countdown is complete
    return () => {
      clearInterval(countdownInterval);
    };
  };

  useEffect(() => {
    if (modalToShow === 'countdown' && !showIntro) {
      setIsModalOverlayVisible(true);
      setIsModalSmall(false);
      updateModalTimeContent();
    } else if (modalToShow === 'well-done') {
      setIsModalSmall(true);
      setIsModalOverlayVisible(false);
      setModalContent(
        <>
          <div className="modal-center vertical">
            <div><span>Well done!</span></div>
            {
                isGameBelongsToTournament && scenarioLevels.length === levelNumber
                  ? <button onClick={() => window.close()} className="button-blue button-view-summary">Close</button>
                  : <button onClick={() => navigate(`/classification/level/${levelNumber}/points/`)} className="button-blue button-view-summary">{isGameBelongsToTournament && scenarioLevels.length !== levelNumber ? 'Next Level' : 'View Summary'}</button>
            }
          </div>
        </>
      );
    }
  }, [modalToShow, showIntro, levelNumber, isGameBelongsToTournament]); 

  const handleIntroClose = () => {
    setShowIntro(false);    
  }

  useEffect(() => {  
    if (isPlaying && soundStatus === 'enabled') {
      gameProcessEffect.current.volume = 0.09;
      gameProcessEffect.current.loop = true;
      gameProcessEffect.current.play().catch(e => console.log('Error playing game process sound:', e));
    } else {
      gameProcessEffect.current.pause();
    }
  
    return () => {
      gameProcessEffect.current.pause();
    };
  }, [isPlaying, soundStatus]);

  useEffect(() => {
    const playClickSound = () => {
      if (soundStatus === 'enabled') {
        clickEffect.current.play().catch(e => console.error('Error playing click sound:', e));
      }
    };

    document.addEventListener('click', playClickSound);

    return () => {
      document.removeEventListener('click', playClickSound);
    };
  }, [soundStatus]);

  const handleSortedItemClick = (item, container, callback) => {
    const onSuccess = (result) => {
      callback(null, result);

      const footerMessagePrefix = '';

      const footerMessageMiddle = result.is_correct
        ? ' belongs in '
        : ' does NOT belong in ';

      const footerMessageSuffix = '.';

      const footerMessage = <p>{footerMessagePrefix}<b>{item.name}</b>{footerMessageMiddle}<b>{container.name}</b>{footerMessageSuffix}</p>;

      if(result.is_correct) {
        answerCorrectEffect.current.play().catch(e => console.log('Error playing answer correct sound:', e));

        setShouldScorePlaySound(true);
        showFooterMessage('success', footerMessage);
        setTotalScore(prevTotalScore => prevTotalScore + result.score_points);
        setItemsToBeSorted(prevItems => {
          const updatedItems = prevItems.filter(prevItem => prevItem.id !== item.id);
          if(!updatedItems.length) {
            updateGameSessionMutation.mutate({
              id: currentGameSessionId,
              levelNumber,
              levelStatus: 'completed',
              isSessionCompleted: true,
            });

            setIsPlaying(false);
            setModalToShow('well-done');
          }
          return updatedItems;
        });
      } else {
        answerWrongEffect.current.play().catch(e => console.log('Error playing answer wrong sound:', e));

        showFooterMessage('error', footerMessage);
      }
    };
  
    decisionMutation.mutate({
      user: user?.id,
      game: gameId,
      question: item.id,
      selected_option: container.id,
      time_elapsed: time,
      gameSessionId: currentGameSessionId
    }, {
      onSuccess: onSuccess,
      onError: (error) => {
        console.log('Error: ', error);
      }
    });
  };

  return (
    <div className="game-level-wrapper">
      {
        showIntro && (
          <Intro
            leftTitle={leftIntroTitle}
            leftSubtitle={leftIntroSubtitle}
            leftDescription={leftIntroDescription}
            rightTitle={rightIntroTitle}
            rightSubtitle={rightIntroSubtitle}
            rightDescription={rightIntroDescription}
            onShowingCompleted={handleIntroClose}
            isContentCentered={isIntroContentCentered}
            isSubtitleContentReversed={isIntroSubtitleContentReversed}
          />
      )}
      <Header 
        time={time}
        totalScore={totalScore}
        shouldScorePlaySound={shouldScorePlaySound}
      />
      <section className="main-section main-section-sorting" style={{backgroundImage: `url(${Background})`}}>
        <div className="container columns">
          <Column
            side='left'
            options={options[0]}
            levelNumber={levelNumber}
            prePlacedItems={placedItems}
            gameType="Account Classification"
            isGameSorted={true}
            onSortedItemClick={handleSortedItemClick}
          />
          <main className="content">
            <img src={NikeLogo} alt="Nike" className="sorting-company-logo"/>
          </main>
          <Column
            side='right'
            options={options[1]}
            levelNumber={levelNumber}
            prePlacedItems={placedItems}
            gameType="Account Classification"
            isGameSorted={true}
            onSortedItemClick={handleSortedItemClick}
          />
        </div>
      </section>
      <footer className="game-footer">
        {isFooterMessageShown && (
          <FooterMessage
            type={footerMessageType}
            description={footerMessageDescription}
          />
        )}
      </footer>
      {modalToShow && (
        <Modal
          content={modalContent}
          isModalOverlayVisible={isModalOverlayVisible}
          isModalSmall={isModalSmall}
        />
      )}
    </div>
  );
}
